import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider, Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useAppDispatch, useAppSelector } from './store';
import { checkAuth } from './store/slices/authSlice';
import { InvoiceItem, defaultInvoiceItem } from './config/types';
import AppRoutes from './routes/index';
import Layout from './components/Layout/index';
import { createExcelFromTemplate } from './services/excelService';
import theme from './theme';

// Define interfaces
interface Entry {
  [key: string]: any; // This allows for dynamic properties
  broker: string;
}

function App() {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);

  const handleFormSubmit = async (entries: Entry[]) => {
    try {
      // Add default values to each entry but preserve their broker
      const entriesWithDefaults = entries.map((entry: Entry) => ({
        ...defaultInvoiceItem,  // Add default values first
        ...entry  // Override with actual entry values (including broker)
      })) as InvoiceItem[];
      
      // Create Excel file from template using the Loads template
      // Use the broker from the first entry
      const broker = entriesWithDefaults[0]?.broker?.toLowerCase();
      if (!broker) {
        throw new Error('No broker specified for the entries');
      }
      const excelBlob = await createExcelFromTemplate(broker, entriesWithDefaults);
      return excelBlob;
    } catch (error) {
      console.error('Error saving data:', error);
      throw error;
    }
  };

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Layout>
          <AppRoutes 
            onFormSubmit={handleFormSubmit}
          />
        </Layout>
      </Router>
    </ThemeProvider>
  );
}

export default App;
